import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'AuthContext';
import useClubContext from 'contexts/Club';
import {
  getPosts,
  createPost as createPostService,
  updatePost as updatePostService,
  deletePost as deletePostService,
  addReaction as addReactionService,
  removeReaction as removeReactionService,
} from 'services/posts/posts.service';
import {
  getBlockedUsers,
  blockUser as blockUserService,
  unblockUser as unblockUserService,
} from 'services/moderation/moderation.service';
import { connectSocket, joinClubRoom, leaveClubRoom } from 'services/socket';
import { markAsRead as markAsReadService } from 'services/feed/feed.service';
import { setBadge, clearBadge } from 'services/badge';
import FeedContext from './FeedContext';
import { fetchFeedInitial, useFeedInitial } from 'hooks/useFeed';

const FEED_POSTS_STORAGE_KEY = 'feed_posts';

const getStoredFeedPosts = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(FEED_POSTS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const posts = Array.isArray(parsed.posts) ? parsed.posts : [];
    return {
      clubId: parsed.clubId,
      userId: parsed.userId,
      posts,
      hasMore: Boolean(parsed.hasMore),
      oldestPostId: parsed.oldestPostId ?? null,
    };
  } catch {
    return null;
  }
};

const setStoredFeedPosts = (clubId, userId, payload) => {
  if (typeof window === 'undefined' || !clubId || !userId || !payload) return;
  try {
    sessionStorage.setItem(FEED_POSTS_STORAGE_KEY, JSON.stringify({
      clubId,
      userId,
      posts: Array.isArray(payload.posts) ? payload.posts : [],
      hasMore: Boolean(payload.hasMore),
      oldestPostId: payload.oldestPostId ?? null,
    }));
  } catch {
    // ignore quota or parse errors
  }
};

let _feedRestoreCache = null;

// ******************STATE VALUES**********************
const FeedProvider = ({ children }) => {
  const { user } = useAuth();
  const { currentClub } = useClubContext();
  const queryClient = useQueryClient();
  const clubId = currentClub?.id;
  const userId = user?.uid;
  const [posts, setPosts] = useState(() => {
    if (!_feedRestoreCache) _feedRestoreCache = getStoredFeedPosts();
    return _feedRestoreCache?.posts ?? [];
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [lastReadTimestamp, setLastReadTimestamp] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(() => _feedRestoreCache?.hasMore ?? false);
  const [oldestPostId, setOldestPostId] = useState(() => _feedRestoreCache?.oldestPostId ?? null);
  const [showActivity, setShowActivity] = useState(true);
  const [replyingTo, setReplyingToState] = useState(null);
  const [editingPost, setEditingPostState] = useState(null);
  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const postRefs = useRef({});
  const lastReadTimestampRef = useRef(null);
  const showActivityRef = useRef(true);
  const blockedUserIdsRef = useRef([]);

  useEffect(() => {
    blockedUserIdsRef.current = blockedUserIds;
  }, [blockedUserIds]);

  useEffect(() => {
    let cancelled = false;
    const loadBlocks = async () => {
      if (!user?.uid) {
        setBlockedUserIds([]);
        return;
      }
      try {
        const data = await getBlockedUsers(user.uid);
        if (!cancelled) {
          setBlockedUserIds(data.blockedUserIds || []);
        }
      } catch (err) {
        console.error('Error loading blocked users:', err);
      }
    };
    loadBlocks();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!blockedUserIds.length) return;
    setPosts((prev) =>
      prev.filter((post) => !blockedUserIds.includes(post.authorId || post.author?.uid))
    );
  }, [blockedUserIds]);

  const clearReplyingTo = useCallback(() => {
    setReplyingToState(null);
  }, []);

  const clearEditingPost = useCallback(() => {
    setEditingPostState(null);
  }, []);

  const setReplyingTo = useCallback((target) => {
    setEditingPostState(null);
    setReplyingToState(target);
  }, []);

  const setEditingPost = useCallback((post) => {
    setReplyingToState(null);
    setEditingPostState(post);
  }, []);

  const {
    data: feedData,
    isLoading: loading,
    error: queryError,
  } = useFeedInitial(clubId, userId, {
    limit: 25,
    includeActivity: showActivity,
  });

  const fetchPosts = useCallback(async () => {
    if (!clubId || !userId) return;
    const data = await fetchFeedInitial(clubId, userId, {
      limit: 25,
      includeActivity: showActivity,
      cacheBust: Date.now(),
      noStore: true,
    });
    queryClient.setQueryData(['feed', clubId, userId, showActivity], data);
  }, [clubId, queryClient, showActivity, userId]);

  // Sync query data to state so rest of provider (load more, mutations, socket) works
  useEffect(() => {
    if (!feedData) return;
    const postsData = feedData.posts || [];
    setPosts(Array.isArray(postsData) ? postsData : []);
    setHasMore(feedData.hasMore ?? false);
    setOldestPostId(feedData.nextBeforeId ?? null);
    const lastReadAt = feedData.readStatus?.lastReadAt
      ? new Date(feedData.readStatus.lastReadAt)
      : null;
    const cacheKey = currentClub && user ? `feedLastRead_${currentClub.id}_${user.uid}` : null;
    const cachedTimestamp = cacheKey ? localStorage.getItem(cacheKey) : null;
    const effectiveLastRead = (cachedTimestamp && (!lastReadAt || new Date(cachedTimestamp) > lastReadAt))
      ? new Date(cachedTimestamp)
      : lastReadAt;
    setLastReadTimestamp(effectiveLastRead);
    const lastRead = effectiveLastRead || (cachedTimestamp ? new Date(cachedTimestamp) : null);
    if (lastRead) {
      const unread = postsData.filter(post => new Date(post.created_at) > lastRead).length;
      setUnreadCount(unread);
    } else {
      setUnreadCount(postsData.length);
    }
  }, [feedData, currentClub, user]);

  useEffect(() => {
    if (queryError) {
      setError('Failed to fetch posts');
    } else {
      setError(null);
    }
  }, [queryError]);

  // Clear restored state if stored feed was for a different club/user
  useEffect(() => {
    if (!currentClub?.id || !user?.uid) return;
    const stored = getStoredFeedPosts();
    if (stored && (stored.clubId !== currentClub.id || stored.userId !== user.uid)) {
      setPosts([]);
      setHasMore(false);
      setOldestPostId(null);
    }
  }, [currentClub?.id, user?.uid]);

  useEffect(() => {
    setReplyingToState(null);
    setEditingPostState(null);
  }, [currentClub?.id]);

  // Persist feed list so we can restore on remount (avoid "no messages" flash)
  useEffect(() => {
    if (!currentClub?.id || !user?.uid) return;
    setStoredFeedPosts(currentClub.id, user.uid, { posts, hasMore, oldestPostId });
  }, [currentClub?.id, user?.uid, posts, hasMore, oldestPostId]);

  useEffect(() => () => { _feedRestoreCache = null }, []);

  // Load more posts (older posts) when scrolling to top
  const loadMorePosts = useCallback(async () => {
    if (!currentClub || !user || !hasMore || loadingMore || !oldestPostId) return;

    try {
      setLoadingMore(true);
      
      // Fetch older posts
      const postsResponse = await getPosts(currentClub.id, {
        limit: 25,
        beforeId: oldestPostId,
        includeActivity: showActivity,
        userId: user.uid,
      });
      
      const newPosts = postsResponse.posts || [];
      const hasMorePosts = postsResponse.hasMore || false;
      const nextBeforeId = postsResponse.nextBeforeId || null;
      
      // Append older posts to the end of the array (they're older, so go at the end)
      setPosts(prev => [...prev, ...newPosts]);
      setHasMore(hasMorePosts);
      setOldestPostId(nextBeforeId);
    } catch (err) {
      console.error('Error loading more posts:', err);
      // Don't set error state - just log it, user can try again
    } finally {
      setLoadingMore(false);
    }
  }, [currentClub, user, hasMore, loadingMore, oldestPostId, showActivity]);

  // ******************EFFECTS/REACTIONS**********************
  // Keep ref in sync with lastReadTimestamp state
  useEffect(() => {
    lastReadTimestampRef.current = lastReadTimestamp;
  }, [lastReadTimestamp]);

  useEffect(() => {
    showActivityRef.current = showActivity;
    if (!showActivity) {
      setPosts(prev => prev.filter(post => !post.isActivity));
    }
  }, [showActivity]);

  // Socket.io connection and room management
  const socketRef = useRef(null);
  const eventHandlersRef = useRef({});
  const processedPostIdsRef = useRef(new Set()); // Track recently processed post IDs to prevent duplicates

  useEffect(() => {
    if (!user || !currentClub) return;

    const setupSocket = async () => {
      try {
        console.log(`[FeedProvider] Setting up socket for club ${currentClub.id} (user: ${user.uid})`);
        const socket = await connectSocket();
        socketRef.current = socket;
        
        console.log(`[FeedProvider] Socket connected, joining club room ${currentClub.id}`);
        // Join club room
        joinClubRoom(currentClub.id);

        // Create event handlers (store them so we can remove them later)
        const handlePostCreated = (postData) => {
          const postId = postData.id?.toString();
          const authorId = postData.authorId || postData.author?.uid;
          if (authorId && blockedUserIdsRef.current.includes(authorId)) {
            return;
          }
          
          if (!showActivityRef.current && postData.isActivity) {
            return;
          }
          
          // Check if we've already processed this post ID (prevent duplicate processing)
          if (processedPostIdsRef.current.has(postId)) {
            return; // Already processed this post, ignore duplicate event
          }
          
          // Mark this post as processed
          processedPostIdsRef.current.add(postId);
          
          // Clean up old processed IDs after 5 seconds to prevent memory leak
          setTimeout(() => {
            processedPostIdsRef.current.delete(postId);
          }, 5000);
          
          // Check if post already exists in state before processing
          setPosts(prev => {
            // First, check if there's an optimistic post to replace
            // Match by authorId and text (since optimistic posts have temp IDs)
            const optimisticPost = prev.find(p => 
              p.isOptimistic && 
              p.authorId === postData.authorId && 
              p.text === postData.text
            );
            
            if (optimisticPost) {
              // Replace optimistic post with real one
              return prev.map(p => 
                p.tempId === optimisticPost.tempId 
                  ? { ...postData, isOptimistic: false }
                  : p
              );
            }
            
            // No optimistic post found, check if post already exists (avoid duplicates)
            const existingPost = prev.find(p => {
              const prevId = p.id?.toString();
              return prevId === postId;
            });
            
            if (existingPost) {
              // Post already exists in state, don't add duplicate
              return prev;
            }
            
            // Post is new, add it
            return [postData, ...prev];
          });
          
          // Update unread count separately, only if post is newer than last read
          // This ensures it only happens once per post
          const lastRead = lastReadTimestampRef.current;
          if (!lastRead) {
            setUnreadCount(prevCount => {
              return prevCount + 1;
            });
          } else {
            const postDate = new Date(postData.created_at);
            if (postDate > lastRead) {
              setUnreadCount(prevCount => {
                return prevCount + 1;
              });
            }
          }
        };

        const handleReactionAdded = (data) => {
          setPosts(prev => prev.map(post => {
            if (post.id === data.postId) {
              return {
                ...post,
                reactions: data.reactions,
              };
            }
            return post;
          }));
        };

        const handleReactionRemoved = (data) => {
          setPosts(prev => prev.map(post => {
            if (post.id === data.postId) {
              return {
                ...post,
                reactions: data.reactions,
              };
            }
            return post;
          }));
        };

        const handlePostUpdated = (postData) => {
          if (!showActivityRef.current && postData.isActivity) {
            return;
          }
          setPosts(prev => prev.map(post => {
            if (post.id === postData.id) {
              return postData;
            }
            return post;
          }));
        };

        const handlePostDeleted = (data) => {
          const deletedPostId = data?.postId?.toString();
          if (!deletedPostId) return;
          setPosts(prev => prev.filter(post => post.id?.toString() !== deletedPostId));
        };

        // Remove any existing listeners first to prevent duplicates
        if (eventHandlersRef.current['post:created']) {
          socket.off('post:created', eventHandlersRef.current['post:created']);
        }
        if (eventHandlersRef.current['post:updated']) {
          socket.off('post:updated', eventHandlersRef.current['post:updated']);
        }
        if (eventHandlersRef.current['post:deleted']) {
          socket.off('post:deleted', eventHandlersRef.current['post:deleted']);
        }
        if (eventHandlersRef.current['reaction:added']) {
          socket.off('reaction:added', eventHandlersRef.current['reaction:added']);
        }
        if (eventHandlersRef.current['reaction:removed']) {
          socket.off('reaction:removed', eventHandlersRef.current['reaction:removed']);
        }

        // Store handlers for cleanup
        eventHandlersRef.current = {
          'post:created': handlePostCreated,
          'post:updated': handlePostUpdated,
          'post:deleted': handlePostDeleted,
          'reaction:added': handleReactionAdded,
          'reaction:removed': handleReactionRemoved,
        };

        // Register event listeners
        socket.on('post:created', handlePostCreated);
        socket.on('post:updated', handlePostUpdated);
        socket.on('post:deleted', handlePostDeleted);
        socket.on('reaction:added', handleReactionAdded);
        socket.on('reaction:removed', handleReactionRemoved);
        
        // Listen for reconnect events to rejoin room
        const handleReconnect = () => {
          console.log(`[FeedProvider] Socket reconnected, rejoining club room ${currentClub.id}`);
          joinClubRoom(currentClub.id);
        };
        
        socket.on('reconnect', handleReconnect);
        eventHandlersRef.current['reconnect'] = handleReconnect;
        
      } catch (err) {
        console.error('[FeedProvider] Error setting up socket:', err);
        socketRef.current = null;
      }
    };

    setupSocket();

    return () => {
      // Cleanup: remove all event listeners and disconnect
      const socket = socketRef.current;
      if (socket) {
        console.log(`[FeedProvider] Cleaning up socket listeners for club ${currentClub?.id}`);
        // Remove all event listeners
        Object.entries(eventHandlersRef.current).forEach(([event, handler]) => {
          socket.off(event, handler);
        });
        eventHandlersRef.current = {};
        
        // Leave club room and disconnect
        if (currentClub) {
          console.log(`[FeedProvider] Leaving club room ${currentClub.id}`);
          leaveClubRoom(currentClub.id);
        }
        
        // Note: We don't call disconnectSocket() here because the socket service
        // manages the global socket instance. We just clean up our listeners.
        socketRef.current = null;
      }
    };
  }, [user, currentClub]); // Removed lastReadTimestamp from dependencies

  // No reset effect: when club/user changes, the feed query key changes so React Query
  // returns different data (or refetches). The sync effect above runs when feedData
  // updates and populates state. A separate reset effect would run after sync and clear
  // posts, leaving the feed empty on remount (e.g. navigating between Dashboard and Feed).

  // ******************SETTERS**********************
  const createPost = useCallback(async (postData) => {
    if (!user || !currentClub) return;

    // Create optimistic post immediately
    const tempId = `temp-${Date.now()}`;
    const optimisticPost = {
      id: tempId,
      text: postData.text,
      authorId: user.uid,
      authorName: user.displayName || user.email,
      author: {
        uid: user.uid,
        displayName: user.displayName || user.email,
        photoUrl: user.photoURL || null,
      },
      clubId: currentClub.id,
      created_at: new Date().toISOString(),
      reactions: [],
      isSpoiler: postData.isSpoiler || false,
      isOptimistic: true,
      tempId: tempId, // Store tempId for matching later
      images: postData.images || [],
    };

    // Add optimistic post to state immediately
    setPosts(prev => [optimisticPost, ...prev]);

    try {
      const newPost = await createPostService(currentClub.id, {
        ...postData,
        authorId: user.uid,
        authorName: user.displayName || user.email,
      });
      
      // Socket.io event will replace optimistic post, but if it doesn't arrive,
      // we'll replace it manually after a short delay
      setTimeout(() => {
        setPosts(prev => {
          // Check if optimistic post still exists (Socket.io didn't replace it)
          const stillHasOptimistic = prev.some(p => p.tempId === tempId);
          if (stillHasOptimistic) {
            // Replace optimistic post with real one
            return prev.map(p => p.tempId === tempId ? { ...newPost, isOptimistic: false } : p);
          }
          return prev;
        });
      }, 2000);
      
      return newPost;
    } catch (err) {
      // Remove optimistic post on error
      setPosts(prev => prev.filter(p => p.tempId !== tempId));
      setError('Failed to create post');
      console.error('Error creating post:', err);
      throw err;
    }
  }, [user, currentClub]);

  const createReply = useCallback(async (parentPostId, replyData) => {
    if (!user || !currentClub) return;

    // Create optimistic reply immediately
    const tempId = `temp-reply-${Date.now()}`;
    const optimisticReply = {
      id: tempId,
      text: replyData.text,
      parentPostId,
      authorId: user.uid,
      authorName: user.displayName || user.email,
      author: {
        uid: user.uid,
        displayName: user.displayName || user.email,
        photoUrl: user.photoURL || null,
      },
      clubId: currentClub.id,
      created_at: new Date().toISOString(),
      reactions: [],
      isSpoiler: replyData.isSpoiler || false,
      isOptimistic: true,
      tempId: tempId,
      images: replyData.images || [],
    };

    // Add optimistic reply to state immediately
    setPosts(prev => [optimisticReply, ...prev]);

    try {
      const newReply = await createPostService(currentClub.id, {
        ...replyData,
        authorId: user.uid,
        authorName: user.displayName || user.email,
        parentPostId,
      });
      
      // Socket.io event will replace optimistic reply, but if it doesn't arrive,
      // we'll replace it manually after a short delay
      setTimeout(() => {
        setPosts(prev => {
          // Check if optimistic reply still exists (Socket.io didn't replace it)
          const stillHasOptimistic = prev.some(p => p.tempId === tempId);
          if (stillHasOptimistic) {
            // Replace optimistic reply with real one
            return prev.map(p => p.tempId === tempId ? { ...newReply, isOptimistic: false } : p);
          }
          return prev;
        });
      }, 2000);
      
      return newReply;
    } catch (err) {
      // Remove optimistic reply on error
      setPosts(prev => prev.filter(p => p.tempId !== tempId));
      setError('Failed to create reply');
      console.error('Error creating reply:', err);
      throw err;
    }
  }, [user, currentClub]);

  const updatePost = useCallback(async (postId, data) => {
    if (!user || !currentClub) return;

    try {
      const updatedPost = await updatePostService(currentClub.id, postId, user.uid, data);
      setPosts(prev => prev.map(post => post.id === postId ? updatedPost : post));
      return updatedPost;
    } catch (err) {
      setError('Failed to update post');
      console.error('Error updating post:', err);
      throw err;
    }
  }, [user, currentClub]);

  const deletePost = useCallback(async (postId) => {
    if (!user || !currentClub) return;

    const previousPosts = posts;
    setPosts(prev => prev.filter(post => post.id !== postId));

    try {
      await deletePostService(currentClub.id, postId, user.uid);
    } catch (err) {
      setPosts(previousPosts);
      setError('Failed to delete post');
      console.error('Error deleting post:', err);
      throw err;
    }
  }, [user, currentClub, posts]);

  const removePostFromFeed = useCallback((postId) => {
    setPosts((prev) => prev.filter((post) => post.id !== postId));
  }, []);

  const hidePostsByAuthor = useCallback((authorId) => {
    if (!authorId) return;
    setBlockedUserIds((prev) => (prev.includes(authorId) ? prev : [...prev, authorId]));
    setPosts((prev) =>
      prev.filter((post) => (post.authorId || post.author?.uid) !== authorId)
    );
  }, []);

  const blockUser = useCallback(async (blockedUserId) => {
    if (!user?.uid || !blockedUserId) return;
    await blockUserService(user.uid, blockedUserId);
    hidePostsByAuthor(blockedUserId);
  }, [user?.uid, hidePostsByAuthor]);

  const unblockUser = useCallback(async (blockedUserId) => {
    if (!user?.uid || !blockedUserId) return;
    await unblockUserService(user.uid, blockedUserId);
    setBlockedUserIds((prev) => prev.filter((id) => id !== blockedUserId));
    await fetchPosts();
  }, [fetchPosts, user?.uid]);

  const addReaction = useCallback(async (postId, emoji) => {
    if (!user || !currentClub) return;

    // Optimistically update local state
    setPosts(prev => prev.map(post => {
      if (post.id === postId) {
        const existingReactions = post.reactions || [];
        // Check if user already has this reaction (shouldn't happen, but just in case)
        const hasReaction = existingReactions.some(r => r.userId === user.uid && r.emoji === emoji);
        if (hasReaction) {
          return post; // Already reacted, don't add duplicate
        }
        // Add new reaction optimistically
        const newReaction = {
          id: Date.now(), // Temporary ID
          postId,
          userId: user.uid,
          emoji,
          user: {
            uid: user.uid,
            displayName: user.displayName,
            photoUrl: user.photoURL,
          },
          created_at: new Date().toISOString(),
        };
        return {
          ...post,
          reactions: [...existingReactions, newReaction],
        };
      }
      return post;
    }));

    try {
      await addReactionService(currentClub.id, postId, emoji, user.uid);
      // Socket.io will handle updating state with server data, but local update is already done
    } catch (err) {
      // Revert optimistic update on error
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const existingReactions = post.reactions || [];
          // Remove the temporary reaction (has temp ID or matches user/emoji without a real ID)
          return {
            ...post,
            reactions: existingReactions.filter(r => {
              // Keep reactions that don't match this user/emoji combo, or have a real ID (not temp)
              return !(r.userId === user.uid && r.emoji === emoji && (typeof r.id === 'number' && r.id > 1000000000000));
            }),
          };
        }
        return post;
      }));
      setError('Failed to add reaction');
      console.error('Error adding reaction:', err);
      throw err;
    }
  }, [user, currentClub]);

  const removeReaction = useCallback(async (postId, emoji) => {
    if (!user || !currentClub) return;

    // Optimistically update local state
    setPosts(prev => prev.map(post => {
      if (post.id === postId) {
        const existingReactions = post.reactions || [];
        return {
          ...post,
          reactions: existingReactions.filter(r => !(r.userId === user.uid && r.emoji === emoji)),
        };
      }
      return post;
    }));

    try {
      await removeReactionService(currentClub.id, postId, emoji, user.uid);
      // Socket.io will handle updating state with server data, but local update is already done
    } catch (err) {
      // On error, we'd need to refetch, but for now just show error
      // In a production app, you might want to refetch the post's reactions
      setError('Failed to remove reaction');
      console.error('Error removing reaction:', err);
      // Refetch posts to get correct state
      fetchPosts();
      throw err;
    }
  }, [user, currentClub, fetchPosts]);

  // ******************UTILITY FUNCTIONS**********************
  const scrollToPost = useCallback((postId) => {
    const postElement = postRefs.current[postId];
    if (postElement) {
      postElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight briefly
      postElement.style.transition = 'background-color 0.3s';
      postElement.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
      setTimeout(() => {
        postElement.style.backgroundColor = '';
      }, 2000);
    }
  }, []);

  const registerPostRef = useCallback((postId, element) => {
    if (element) {
      postRefs.current[postId] = element;
    } else {
      delete postRefs.current[postId];
    }
  }, []);

  const markAsRead = useCallback(async (timestamp = null) => {
    if (!user || !currentClub) return;

    const readTimestamp = timestamp || new Date();

    try {
      // Update database
      await markAsReadService(currentClub.id, user.uid, readTimestamp);
      
      // Update local state
      setLastReadTimestamp(readTimestamp);
      
      // Update localStorage cache
      const cacheKey = `feedLastRead_${currentClub.id}_${user.uid}`;
      localStorage.setItem(cacheKey, readTimestamp.toISOString());
      
      // Recalculate unread count
      // Safety check: ensure posts is an array
      const unread = Array.isArray(posts) 
        ? posts.filter(post => {
            const postDate = new Date(post.created_at);
            return postDate > readTimestamp;
          }).length
        : 0;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error marking feed as read:', err);
      // Still update local cache even if API fails
      const cacheKey = `feedLastRead_${currentClub.id}_${user.uid}`;
      localStorage.setItem(cacheKey, readTimestamp.toISOString());
      setLastReadTimestamp(readTimestamp);
    }
  }, [user, currentClub, posts]);

  // Update PWA badge when unreadCount changes
  useEffect(() => {
    if (unreadCount > 0) {
      setBadge(unreadCount);
    } else {
      clearBadge();
    }
  }, [unreadCount]);

  // ******************EXPORTS**********************
  return (
    <FeedContext.Provider
      value={{
        posts,
        loading,
        loadingMore,
        error,
        unreadCount,
        lastReadTimestamp,
        hasMore,
        showActivity,
        replyingTo,
        editingPost,
        fetchPosts,
        loadMorePosts,
        createPost,
        createReply,
        updatePost,
        deletePost,
        removePostFromFeed,
        hidePostsByAuthor,
        blockUser,
        unblockUser,
        blockedUserIds,
        addReaction,
        removeReaction,
        markAsRead,
        scrollToPost,
        registerPostRef,
        setShowActivity,
        setReplyingTo,
        clearReplyingTo,
        setEditingPost,
        clearEditingPost,
      }}
    >
      {children}
    </FeedContext.Provider>
  );
};

export default FeedProvider;

